import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Copy, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Badge } from '@/components/atoms/badge';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import {
  usePartnerApiKeys,
  useCreatePartnerApiKey,
  useRevokePartnerApiKey,
} from '@/hooks/partnerApiKeys/usePartnerApiKeys';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { useHasPermission } from '@/hooks/usePermissions';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import type {
  CreatedPartnerApiKey,
  PartnerApiKey,
  PartnerApiKeyScope,
  PartnerApiKeyStatus,
} from '@/types/partnerApiKey';
import { PartnerApiSettingsShell } from '@/components/organisms/settings-partner-api/PartnerApiSettingsShell';

export const Route = createFileRoute('/_dashboard/dashboard/settings/api-keys')({
  component: ApiKeysPage,
});

const STATUS_VARIANT: Record<
  PartnerApiKeyStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  active: 'default',
  revoked: 'destructive',
  expired: 'secondary',
};

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success('Copied to clipboard'),
    () => toast.error('Could not copy'),
  );
}

function ApiKeysPage() {
  const { data: keys = [], isLoading } = usePartnerApiKeys();
  const companyTz = useCompanyTimezone();
  const canEdit = useHasPermission('company:edit');
  const [dialogOpen, setDialogOpen] = useState(false);
  // The freshly-created key, held only long enough to show its secret once.
  const [createdKey, setCreatedKey] = useState<CreatedPartnerApiKey | null>(null);

  return (
    <PartnerApiSettingsShell activeTab="api-keys">
      <div className="mx-auto max-w-3xl px-5 py-5">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">API credentials</h2>
            <p className="text-sm text-muted-foreground">
              Create credentials for an integration partner to read and write this account through
              the Notifi Partner API. Treat each key like a password.
            </p>
          </div>
          {canEdit && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="mr-1.5 size-4" /> Create API key
            </Button>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : keys.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            No API keys yet. Create one to connect an integration partner.
          </div>
        ) : (
          <div className="space-y-3">
            {keys.map((key) => (
              <ApiKeyRow key={key.id} apiKey={key} tz={companyTz} canEdit={canEdit} />
            ))}
          </div>
        )}

        <CreateDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onCreated={(created) => {
            setDialogOpen(false);
            setCreatedKey(created);
          }}
        />

        <SecretDialog created={createdKey} onClose={() => setCreatedKey(null)} />
      </div>
    </PartnerApiSettingsShell>
  );
}

function ApiKeyRow({
  apiKey,
  tz,
  canEdit,
}: {
  apiKey: PartnerApiKey;
  tz?: string;
  canEdit: boolean;
}) {
  const revoke = useRevokePartnerApiKey();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const lastUsedLabel = apiKey.lastUsedAt
    ? formatDateTimeInTimeZone(apiKey.lastUsedAt, tz, DATE_OPTS)
    : 'Never';

  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{apiKey.label || 'Untitled key'}</span>
            <Badge variant={STATUS_VARIANT[apiKey.status]}>{apiKey.status}</Badge>
            {apiKey.scopes.map((scope) => (
              <Badge key={scope} variant="outline">
                {scope}
              </Badge>
            ))}
          </div>
          <code className="mt-1 block font-mono text-sm text-muted-foreground">
            {apiKey.keyPrefix}
          </code>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Created {formatDateTimeInTimeZone(apiKey.createdAt, tz, DATE_OPTS)}</span>
            <span>Last used: {lastUsedLabel}</span>
            {apiKey.expiresAt && (
              <span>Expires: {formatDateTimeInTimeZone(apiKey.expiresAt, tz, DATE_OPTS)}</span>
            )}
          </div>
        </div>
        {canEdit && apiKey.status === 'active' && (
          <Button
            variant="ghost"
            size="sm"
            disabled={revoke.isPending}
            onClick={() => setConfirmOpen(true)}
            title="Revoke — the key will stop working immediately"
          >
            <Trash2 className="mr-1 size-4" /> Revoke
          </Button>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              Any integration using <span className="font-medium">{apiKey.label}</span> (
              <code className="font-mono">{apiKey.keyPrefix}</code>) will stop working immediately.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => revoke.mutate(apiKey.id)}
              disabled={revoke.isPending}
            >
              Revoke key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const ALL_SCOPES: { value: PartnerApiKeyScope; label: string; hint: string }[] = [
  { value: 'read', label: 'Read', hint: 'View this account’s data' },
  { value: 'write', label: 'Write', hint: 'Create and update this account’s data' },
];

function CreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: CreatedPartnerApiKey) => void;
}) {
  const create = useCreatePartnerApiKey();
  const [label, setLabel] = useState('');
  // Default to read+write: partner integrations (the whole point of these keys)
  // almost always need write, the backend/docs default to both, and with scope
  // enforcement a read-only default would silently mint a key that 403s on every
  // write. A tenant who wants a restricted key unchecks Write.
  const [scopes, setScopes] = useState<PartnerApiKeyScope[]>(['read', 'write']);

  const reset = () => {
    setLabel('');
    setScopes(['read', 'write']);
  };

  const toggleScope = (scope: PartnerApiKeyScope, checked: boolean) =>
    setScopes((prev) =>
      checked ? [...new Set([...prev, scope])] : prev.filter((s) => s !== scope),
    );

  const submit = () => {
    create.mutate(
      { label: label.trim(), scopes },
      {
        onSuccess: (created) => {
          reset();
          onCreated(created);
        },
      },
    );
  };

  const valid = label.trim().length > 0 && scopes.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API key</DialogTitle>
          <DialogDescription>
            Give the key a label so you can recognize it later, then choose what it can do.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ak-label">Label</Label>
            <Input
              id="ak-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Partner integration"
            />
          </div>
          <div className="space-y-2">
            <Label>Scopes</Label>
            {ALL_SCOPES.map((scope) => (
              <label
                key={scope.value}
                htmlFor={`ak-scope-${scope.value}`}
                className="flex items-start gap-3 rounded-md border p-3"
              >
                <Checkbox
                  id={`ak-scope-${scope.value}`}
                  checked={scopes.includes(scope.value)}
                  onCheckedChange={(checked) => toggleScope(scope.value, checked === true)}
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <span className="text-sm font-medium">{scope.label}</span>
                  <p className="text-xs text-muted-foreground">{scope.hint}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button disabled={!valid || create.isPending} onClick={submit}>
            Create key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SecretDialog({
  created,
  onClose,
}: {
  created: CreatedPartnerApiKey | null;
  onClose: () => void;
}) {
  // Describe the key's actual capability so a restricted (e.g. read-only) key
  // isn't warned about write access it doesn't have.
  const canRead = created?.scopes?.includes('read');
  const canWrite = created?.scopes?.includes('write');
  const capability = canRead && canWrite ? 'read and write' : canWrite ? 'write to' : 'read';
  return (
    <Dialog open={created !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader className="min-w-0">
          <DialogTitle>Copy your API key</DialogTitle>
          <DialogDescription className="break-words">
            This is the only time the full key for{' '}
            <span className="font-medium">{created?.label}</span> will be shown. Copy it now and
            store it somewhere safe — you won&apos;t be able to see it again.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-3 py-2">
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            <span>
              Keep this secret. Anyone with this key can {capability} your account&apos;s data. If
              it leaks, revoke it and create a new one.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto rounded-md border bg-muted px-3 py-2 font-mono text-sm whitespace-nowrap">
              {created?.secret}
            </code>
            <Button
              variant="outline"
              size="icon"
              title="Copy API key"
              onClick={() => created && copyToClipboard(created.secret)}
            >
              <Copy className="size-4" />
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
